import { CantileverButton } from 'components/CantileverButton'
import { KNOWLEDGE_DOC_TAB_ACTION, useGeckoAnalytics } from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import { createDefaultTab } from 'features/knowledgeDocumentViewer/hooks/useTabState'
import { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { Plus, X } from 'lucide-react'
import { useCallback } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { SystemDocumentEntry } from 'types'

const TabsContainer = styled.div`
  width: 100%;
  /* height: 2rem; */
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0 0.5rem;
  gap: 0.125rem;
  overflow-x: auto;
  overflow-y: hidden;

  /* Hide scrollbar but allow scrolling */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TabTitleContainer = styled.span`
  overflow: hidden;
  min-width: 5.25rem;
`

const CloseButton = styled(CantileverButton)`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  visibility: hidden;
`

const AddTabButton = styled(CantileverButton)`
  margin-left: 0.375rem;
`

const Tab = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${(props) =>
    props.$isActive
      ? props.theme.forecast.pageBackground1
      : css`
        color-mix(in srgb, ${props.theme.forecast.pageBackground1}, transparent 60%)
      `};
  user-select: none;
  white-space: nowrap;
  font-size: 0.875rem;
  color: ${(props) =>
    props.$isActive
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};
  max-width: 24rem;

  &:hover {
    ${CloseButton} {
      visibility: visible;
    }

    ${TabTitleContainer} {
      width: calc(100% - 2rem);
    }
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          ${CloseButton} {
            visibility: visible;
          }

          ${TabTitleContainer} {
            width: calc(100% - 2rem);
          }
        `
      : css`
          &:hover {
            background-color: ${(props) =>
              props.theme.forecast.hoverGhostButton};
            color: ${(props) => props.theme.forecast.defaultText};
          }

          &:active {
            background-color: ${(props) =>
              props.theme.forecast.activeGhostButton};
            color: ${(props) => props.theme.forecast.defaultText};
          }
        `}
`

export function DocumentTabs() {
  const { openTabs, removeTab, activeTabId, setActiveTabId, addTab } =
    useKnowledgeDocumentUXContext()
  const { trackEvent } = useGeckoAnalytics()
  const { removeAttachment } = useChatbotAttachments()

  const tabDocumentIds = openTabs
    .map((tab) => {
      const item = tab.history[tab.currentIndex]
      return item.type === 'document' ? item.documentId : null
    })
    .filter((id): id is string => id !== null)

  const { data: tabDocuments } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(tabDocumentIds)

  const handleTabClick = (tabId: string) => {
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
        action: 'switch',
        tab_id: tabId,
        document_id: getTabDocumentId(tab),
        total_tabs: openTabs.length,
      })
      setActiveTabId(tabId)
    }
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    const closedTab = openTabs.find((t) => t.id === tabId)
    const isClosingActiveTab = activeTabId === tabId
    const closedTabDocId = closedTab ? getTabDocumentId(closedTab) : ''

    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'close',
      tab_id: tabId,
      document_id: closedTabDocId,
      total_tabs: openTabs.length,
      was_active: isClosingActiveTab,
    })

    if (closedTabDocId) {
      removeAttachment(closedTabDocId)
    }

    removeTab(tabId)
  }

  const handleAddTab = useCallback(() => {
    const newTab: DocumentTab = createDefaultTab()
    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'add',
      tab_id: newTab.id,
      total_tabs: openTabs.length + 1,
    })
    addTab(newTab)
  }, [addTab, openTabs.length, trackEvent])

  const getTabDocumentId = (tab: DocumentTab): string => {
    const currentItem = tab.history[tab.currentIndex]
    return currentItem.type === 'document' ? currentItem.documentId : ''
  }

  const getReportName = useCallback(
    (tab: DocumentTab): string => {
      const currentItem = tab.history[tab.currentIndex]

      if (
        currentItem.type === 'search' &&
        currentItem.linkedDocuments !== undefined
      ) {
        return `References to ${currentItem.linkedDocuments.referenceText}`
      }

      const documentId = getTabDocumentId(tab)
      if (!documentId) return 'New Tab'
      const documentTitle =
        tabDocuments?.find((entry) => entry.entry.id === documentId)?.entry.data
          .document_title ?? null
      if (!documentTitle) return 'New Tab'
      return toTitleCase(documentTitle)
    },
    [tabDocuments]
  )

  const theme = useTheme()

  return (
    <TabsContainer>
      {openTabs.map((tab) => (
        <Tab
          key={tab.id}
          $isActive={activeTabId === tab.id}
          onClick={() => handleTabClick(tab.id)}>
          <TabTitleContainer>
            <MiddleTruncatedText
              text={getReportName(tab)}
              maxLength={40}
              maxWidth='100%'
              tooltipFontSize='0.875rem'
            />
          </TabTitleContainer>
          <CloseButton
            iconOnly
            ghost
            size='small'
            onClick={(e) => handleCloseTab(e, tab.id)}
            aria-label='Close tab'>
            <X size={18} color={theme.forecast.secondaryText} />
          </CloseButton>
        </Tab>
      ))}
      <AddTabButton
        iconOnly
        ghost
        size='small'
        onClick={handleAddTab}
        title='Add new tab'
        aria-label='Add new tab'>
        <Plus size={18} color={theme.forecast.secondaryText} />
      </AddTabButton>
    </TabsContainer>
  )
}
